import { LoadingOutlined } from '@ant-design/icons'
import { Spin } from 'antd'
import {
  EnrichedOntologyFieldDefinition,
  OntologyValueType,
  useOntology,
} from 'features/dynamicOntology'
import React, { Dispatch, SetStateAction, useCallback, useMemo } from 'react'
import styled from 'styled-components'
import CantileverTheme from 'styles/cantileverTheme'

import { RecursivePropertyView } from './RecursivePropertyView'
import { useSplitObjectState } from './useSplitObjectState'

const SectionTitle = styled.h3`
  color: ${CantileverTheme.colors.gray['600']};
  font-size: 1rem;
  font-weight: 600;
  line-height: 1.5;
  margin: 0 0 0.5rem 0.2rem;
  padding-top: 1.5rem;
  padding-bottom: 0.5rem;
  column-span: all;

  &:first-child {
    padding-top: 0;
  }
`

interface ObjectPropertiesViewProps {
  data: Record<string, unknown>
  setData?: Dispatch<SetStateAction<Record<string, unknown> | undefined>>
  entryType: string
  isLoading?: boolean
  hideNonOntologyProperties?: boolean
}

export const ObjectPropertiesView: React.FC<ObjectPropertiesViewProps> = ({
  data,
  entryType,
  setData,
  isLoading,
  hideNonOntologyProperties,
}) => {
  const ontology = useOntology()

  const relevantOntologyFields = useMemo(
    () =>
      Object.keys(ontology.fields)
        .filter((key) => key.startsWith(`${entryType}.`))
        .map((key) => ontology.fields[key].key)
        .filter((key) => key !== undefined)
        .filter((key) => key !== 'id')
        .filter((key) => !key.startsWith('tags')),
    [entryType, ontology.fields]
  )

  // Map of key to an ontology field def
  const ontologyFields = useMemo<
    Record<string, [string, EnrichedOntologyFieldDefinition]>
  >(() => {
    return Object.fromEntries(
      Object.entries(ontology.fields).map(([key, fieldDef]) => [
        key.split('.')[1],
        [key, fieldDef],
      ])
    )
  }, [ontology.fields])

  const isOntologyField = useCallback(
    (key: string) => relevantOntologyFields.includes(key),
    [relevantOntologyFields]
  )

  const [
    [ontologyProperties, setOntologyProperties],
    [otherProperties, setOtherProperties],
  ] = useSplitObjectState(data, setData, isOntologyField)

  // We want to always show the ontology fields, even if they are not in the data
  // We also want to show them in the order in which the ontology specified them
  const virtualOntologyProperties = useMemo(() => {
    return Object.fromEntries(
      Object.keys(ontologyFields)
        .filter((key) => isOntologyField(key))
        .map((key) => [
          key,
          (ontologyProperties as Record<string, unknown>)?.[key] ?? '',
        ])
    ) as OntologyValueType
  }, [isOntologyField, ontologyFields, ontologyProperties])

  return (
    <Spin spinning={isLoading} indicator={<LoadingOutlined />}>
      {Object.keys(virtualOntologyProperties ?? {}).length > 0 && (
        <>
          <SectionTitle>Properties</SectionTitle>
          <RecursivePropertyView
            object={virtualOntologyProperties}
            setObject={setOntologyProperties}
            indentLevel={0}
            label={undefined}
            ontologyFields={ontologyFields}
          />
        </>
      )}
      {!hideNonOntologyProperties &&
        Object.keys(otherProperties ?? {}).length > 0 && (
          <>
            <SectionTitle>Additional Properties</SectionTitle>
            <RecursivePropertyView
              object={otherProperties}
              setObject={setOtherProperties}
              indentLevel={0}
              label={undefined}
            />
          </>
        )}
    </Spin>
  )
}
